import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppTranslation, useErrTranslation } from 'app/hooks';
import Typo from 'consts/typography';
import { STACKING_ABI, VAULT_LIST, Vault } from 'utils/staking';
import { CustomToken, TBET_TOKEN } from 'connectors/router';
import { useAccount, useConnect, useReadContracts, useSwitchChain, useWriteContract } from 'wagmi';
import { erc20Abi, formatEther, parseUnits } from 'viem';
import { formatAmountV2, formatCryptoBalance } from 'utils/formatAmount';
import { config } from 'index';
import {
  waitForTransactionReceipt,
  readContract,
  type WaitForTransactionReceiptReturnType,
} from '@wagmi/core';
import { showErrorModal } from 'components/Modals/CommonModals';
import { isValidAmount } from '../Modals/WithdrawModal';

interface StakingFormProps {
  onShowLoading: (showLoading: boolean) => void;
  isLoading: boolean;
}

const StakingForm: React.FunctionComponent<StakingFormProps> = ({ onShowLoading, isLoading }) => {
  const t = useAppTranslation();
  const et = useErrTranslation();

  const [option, setOption] = useState<Vault>(VAULT_LIST[0]);
  const [allowance, setAllowance] = useState<string>('0');
  const [activeCoin, setActiveCoin] = useState<CustomToken>(TBET_TOKEN);
  const [amount, setAmount] = useState<string>('');

  const { address, chainId } = useAccount();
  const { connectors, connect } = useConnect();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();

  const { data } = useReadContracts({
    allowFailure: true,
    contracts: [
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address || '0x'],
        chainId: activeCoin.chainId,
      },
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'decimals',
        chainId: activeCoin.chainId,
      },
      {
        address: activeCoin.address,
        abi: erc20Abi,
        functionName: 'allowance',
        args: [address || '0x', option.address],
        chainId: activeCoin.chainId,
      },
    ],
  });

  const getCryptoBalance = () => {
    if (activeCoin.address) {
      return formatCryptoBalance(data?.[0]?.result, activeCoin.decimals);
    }
    return '0';
  };

  const getButtonText = useMemo(() => {
    if (!address) return t('staking.form.connectWallet');
    if (chainId !== activeCoin.chainId) return 'Switch chain';
    if (!Number(getCryptoBalance()) || Number(getCryptoBalance()) < Number(amount)) {
      return t('staking.form.insufficient');
    }
    if (!Number(allowance) || Number(allowance) < Number(amount)) {
      return t('staking.form.approve');
    }
    return t('staking.form.stake');
  }, [allowance, chainId, activeCoin, getCryptoBalance, address, option.id]);

  const getAllowance = () => {
    return data?.[2].result ? formatEther(data?.[2].result) : '0';
  };

  const canClickableButton = useMemo(() => {
    if (!address) return true;
    if (chainId !== activeCoin.chainId) return true;
    if (!Number(getCryptoBalance()) || Number(getCryptoBalance()) < Number(amount)) {
      return false;
    }
    if (!Number(amount)) return false;
    return true;
  }, [address, chainId, activeCoin, amount, getCryptoBalance()]);

  const getConnector = () => {
    const metamask = connectors.find((x) => x.id === 'io.metamask');
    return metamask ? metamask : connectors[0];
  };

  const handleClick = async () => {
    if (!address) {
      connect({ connector: getConnector() });
    } else {
      if (chainId !== activeCoin.chainId) {
        await switchChainAsync({
          chainId: activeCoin.chainId,
        });
      } else if (!Number(allowance) || Number(allowance) < Number(amount)) {
        approve();
      } else {
        staking();
      }
    }
  };

  const approve = useCallback(async () => {
    const res = await writeContractAsync({
      chainId: activeCoin.chainId,
      abi: erc20Abi,
      address: activeCoin.address,
      functionName: 'approve',
      args: [option.address, parseUnits(amount, activeCoin.decimals)],
    });
    onShowLoading(true);
    waitForTransactionReceipt(config, {
      hash: res,
      pollingInterval: 1_000,
    }).then((data: WaitForTransactionReceiptReturnType) => {
      if (data.status === 'success') {
        getNewAllowance();
        onShowLoading(false);
      }
    });
  }, [option.id, amount]);

  const staking = useCallback(async () => {
    if (Number(amount) < 500) {
      showErrorModal({
        content: et('error.validate.stake.amountToLow'),
        onOk: () => {},
        title: et('error.validate.stake.invalidAmount'),
      });
      return;
    }
    const res = await writeContractAsync({
      chainId: activeCoin.chainId,
      abi: STACKING_ABI.abi,
      address: option.address,
      functionName: 'stake',
      args: [parseUnits(amount, activeCoin.decimals)],
    });
    onShowLoading(true);
    waitForTransactionReceipt(config, {
      hash: res,
      pollingInterval: 1_000,
    }).then((data: WaitForTransactionReceiptReturnType) => {
      if (data.status === 'success') {
        getNewAllowance();
        onShowLoading(false);
        setAmount('');
      }
    });
  }, [option.id, amount]);

  useEffect(() => {
    setAllowance(getAllowance());
  }, []);

  const getNewAllowance = useCallback(async () => {
    const res = await readContract(config, {
      address: activeCoin.address,
      abi: erc20Abi,
      functionName: 'allowance',
      args: [address || '0x', option.address],
      chainId: activeCoin.chainId as any,
    });
    setAllowance(formatEther(res));
  }, [option.id]);

  const handleChangeVaultClick = (item: Vault) => {
    setOption(item);
  };

  useEffect(() => {
    getNewAllowance();
    setAmount('');
  }, [option.id]);

  const handleChangeAmount = (event: React.ChangeEvent<HTMLInputElement>) => {
    const regex = /^[0-9]*[.]?[0-9]{0,8}$/;
    const value = event.target.value;

    if ((regex.test(value) && isValidAmount(value)) || value === '') {
      setAmount(value);
    }
  };

  return (
    <div className="staking-form">
      <div className="staking-form__header">
        <h3 className={`${Typo.p2} primary-text-color text-center font-semibold`}>
          {t('staking.form.stakingPool')}
        </h3>
        <div className="staking-form__info">
          <h4 className={`${Typo.p4} primary-text-color font-semibold uppercase`}>
            {t('staking.form.stakingRate')}
          </h4>
          <h2 className="stacking-percent">
            {option.percent} <span>APY</span>
          </h2>
          <div className=" rounded-full stacking-bar ">
            {VAULT_LIST.map((item) => (
              <span
                key={item.id}
                className={`rounded-full stacking-option  ${item.id === option.id ? 'active' : ''}`}
              ></span>
            ))}
          </div>
          <h4 className="upper-case mt-20 primary-text-color font-semibold uppercase">
            {t(option.dateTitle)}
          </h4>
        </div>
      </div>
      <div className="staking-form__form">
        {isLoading ? (
          <span className="spiner-loader"></span>
        ) : (
          <>
            <form>
              <label className="primary-text-color">{t('staking.form.amount')}</label>
              <input
                className="amount-input"
                type="text"
                value={amount}
                onChange={handleChangeAmount}
              />
              <div className="stake-allowance">
                <span> {t('staking.form.allowance')} </span>
                <span>{formatAmountV2(allowance, 8)} $TBET</span>
              </div>
              <div className="percen-group">
                {VAULT_LIST.map((vault) => (
                  <button
                    key={vault.id}
                    type="button"
                    className={`percen-button uppercase ${option.id === vault.id ? 'active' : ''}`}
                    onClick={() => handleChangeVaultClick(vault)}
                  >
                    {t(vault.title)}
                  </button>
                ))}
              </div>
            </form>
            <button
              className={`button-generate ${!canClickableButton ? 'disabled' : ''}`}
              disabled={!canClickableButton}
              onClick={handleClick}
            >
              {getButtonText}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default StakingForm;
